import { Box, Button, Container } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { formatManwon } from '../../utils/formatKRW';
import { logEvent } from '../../utils/ga';
import Adsense from '../Ads/Adsense';
import { ExpertCallout } from '../shared/ExpertCallout';
import { DividendChart } from './Chart/DividendChart';
import { DividendForm } from './Form/DividendForm';
import {
  buildEtfCompareRows,
  calculateDividendResult,
  DividendFormValues,
  DividendResult,
  EtfCompareRow,
  EtfRegion,
  EtfStyle,
  getDividendCallout,
} from './DividendPage.hooks';
import styles from './DividendPage.module.css';

type RegionFilter = 'all' | EtfRegion;
type StyleFilter = 'all' | Extract<EtfStyle, 'growth' | 'covered-call'>;

function DividendPage() {
  const navigate = useNavigate();
  const resultRef = useRef<HTMLDivElement>(null);
  const [result, setResult] = useState<DividendResult>();
  const [formValues, setFormValues] = useState<DividendFormValues>();
  const [regionFilter, setRegionFilter] = useState<RegionFilter>('all');
  const [styleFilter, setStyleFilter] = useState<StyleFilter>('all');

  const handleSubmit = useCallback((values: DividendFormValues) => {
    logEvent('calculator', 'dividend_calculate', 'submit');
    setFormValues(values);
    setResult(calculateDividendResult(values));
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }, []);

  const compareRows = useMemo<EtfCompareRow[]>(
    () => (formValues ? buildEtfCompareRows(formValues) : []),
    [formValues]
  );

  const filteredRows = useMemo(
    () =>
      compareRows.filter((row) => {
        const matchesRegion = regionFilter === 'all' || row.etf.region === regionFilter;
        const matchesStyle = styleFilter === 'all' || row.etf.style === styleFilter;
        return matchesRegion && matchesStyle;
      }),
    [compareRows, regionFilter, styleFilter]
  );

  const callout = useMemo(() => (result ? getDividendCallout(result) : null), [result]);

  return (
    <div className={styles.root}>
      <Helmet>
        <title>배당 ETF 계산기 — 월 배당금 시뮬레이터 | 노후든든</title>
        <meta
          name="description"
          content="배당 ETF 투자로 노후 현금 흐름을 설계하세요. SCHD, VYM, JEPI, TIGER 미국배당다우존스 등 8개 ETF 비교 및 월 배당금 시뮬레이션."
        />
      </Helmet>

      <Container maxWidth="sm">
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>배당 ETF 계산기</h1>
          <p className={styles.pageDesc}>
            배당 ETF 투자로 매달 들어오는 현금 흐름을 설계합니다. 국내 상장 ETF와 미국 직접 투자 ETF를 비교하고,
            내 투자금 기준 예상 월 배당금을 확인하세요.
          </p>
        </div>
        <div className={styles.formCard}>
          <DividendForm onSubmit={handleSubmit} />
        </div>
      </Container>

      {result && formValues && (
        <div ref={resultRef}>
          <Container maxWidth="lg" sx={{ mt: 4 }}>
            <div className={styles.resultSection}>
              <div className={styles.resultHeroCard}>
                <p className={styles.resultHeroLabel}>
                  투자 {formValues.years}년 후 예상 월 배당금 (세후)
                </p>
                <p className={styles.resultHeroAmount}>{formatManwon(result.finalMonthlyNetDividend)}</p>
                <p className={styles.resultHeroSub}>
                  {result.etf.name} · 연 배당률 {(result.etf.dividendYield * 100).toFixed(1)}% → {result.finalYoc.toFixed(1)}% YOC
                </p>
              </div>

              <div className={styles.metricsRow}>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>최종 자산 총액</p>
                  <p className={styles.metricValue}>{formatManwon(result.finalTotalValue)}</p>
                  <p className={styles.metricInterpretation}>원금 + 재투자 배당 + 자본 성장</p>
                </div>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>
                    {formValues.drip ? '연간 세후 배당금' : '누적 실수령 배당금'}
                  </p>
                  <p className={styles.metricValue}>
                    {formatManwon(
                      formValues.drip ? result.finalAnnualNetDividend : result.totalNetDividendReceived
                    )}
                  </p>
                  <p className={styles.metricInterpretation}>
                    {formValues.drip ? `${formValues.years}년 후 연간 실수령` : `${formValues.years}년 합계`}
                  </p>
                </div>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>총 투자 원금</p>
                  <p className={styles.metricValue}>{formatManwon(result.totalInvested)}</p>
                  <p className={styles.metricInterpretation}>초기 + 월 납입 합계</p>
                </div>
              </div>

              {!formValues.drip && (
                <div className={styles.dripInfoBox}>
                  배당금 재투자 OFF 상태입니다. 매년 배당금을 수령하는 시나리오로 계산합니다.
                  배당 재투자(DRIP) ON으로 전환하면 복리 효과로 장기 자산이 크게 늘어납니다.
                </div>
              )}

              <div className={styles.chartCard}>
                <p className={styles.chartTitle}>배당 시뮬레이션</p>
                <DividendChart
                  yearlyData={result.yearlyData}
                  drip={formValues.drip}
                  baseDividendYield={result.etf.dividendYield}
                />
              </div>

              <div className={styles.compareSection}>
                <p className={styles.compareTitle}>ETF 비교 테이블</p>
                <Box className={styles.filterRow}>
                  {[
                    ['all', '전체'],
                    ['KR', '국내 상장'],
                    ['US', '미국 직접'],
                  ].map(([value, label]) => (
                    <Button
                      key={value}
                      variant={regionFilter === value ? 'contained' : 'outlined'}
                      onClick={() => setRegionFilter(value as RegionFilter)}
                      sx={{
                        background: regionFilter === value ? '#000033' : 'transparent',
                        '&:hover': {
                          background: regionFilter === value ? '#001166' : 'transparent',
                        },
                      }}
                    >
                      {label}
                    </Button>
                  ))}
                  {[
                    ['all', '전체 스타일'],
                    ['growth', '배당성장형'],
                    ['covered-call', '고배당·커버드콜'],
                  ].map(([value, label]) => (
                    <Button
                      key={value}
                      variant={styleFilter === value ? 'contained' : 'outlined'}
                      onClick={() => setStyleFilter(value as StyleFilter)}
                      sx={{
                        background: styleFilter === value ? '#000033' : 'transparent',
                        '&:hover': {
                          background: styleFilter === value ? '#001166' : 'transparent',
                        },
                      }}
                    >
                      {label}
                    </Button>
                  ))}
                </Box>

                <div className={styles.tableWrapper}>
                  <p className={styles.tableScrollHint}>← 좌우로 스크롤하여 전체 비교 →</p>
                  <table className={styles.compareTable}>
                    <thead>
                      <tr>
                        <th>ETF</th>
                        <th>지역</th>
                        <th>배당 주기</th>
                        <th>연 배당률</th>
                        <th>연 주가 성장률</th>
                        <th>10년 후 월 배당</th>
                        <th>{formValues.years}년 후 월 배당</th>
                        <th>총 자산</th>
                        <th>YOC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.length === 0 ? (
                        <tr>
                          <td colSpan={9} style={{ textAlign: 'center', padding: '24px', color: '#9ca3af' }}>
                            해당 조건에 맞는 ETF가 없습니다.
                          </td>
                        </tr>
                      ) : (
                        filteredRows.map((row) => (
                          <tr
                            key={row.etf.id}
                            className={row.etf.id === formValues.etfId ? styles.rowSelected : undefined}
                          >
                            <td>{row.etf.ticker}</td>
                            <td>
                              <span className={row.etf.region === 'KR' ? styles.badgeKR : styles.badgeUS}>
                                {row.etf.region === 'KR' ? '국내 상장' : '미국 직투'}
                              </span>
                            </td>
                            <td>
                              <span
                                className={
                                  row.etf.frequency === 'monthly'
                                    ? styles.badgeMonthly
                                    : styles.badgeQuarterly
                                }
                              >
                                {row.etf.frequency === 'monthly' ? '월' : '분기'}
                              </span>
                            </td>
                            <td>{(row.etf.dividendYield * 100).toFixed(1)}%</td>
                            <td>{(row.etf.capitalGrowthRate * 100).toFixed(1)}%</td>
                            <td>{formatManwon(row.monthlyNetDividendAt10y)}</td>
                            <td>{formatManwon(row.monthlyNetDividendFinal)}</td>
                            <td>{formatManwon(row.finalTotalValue)}</td>
                            <td>{row.yocFinal.toFixed(1)}%</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <p className={styles.tableDisclaimer}>
                  * 2024년 기준 데이터. 과거 배당수익률·성장률이 미래를 보장하지 않습니다.
                  국내 상장 ETF는 배당소득세 15.4%, 미국 직접 투자 ETF는 미국 원천징수세 15% 적용.
                  환율 변동은 반영되지 않았습니다.
                </p>
              </div>

              {callout && <ExpertCallout title={callout.title} body={callout.body} cta={callout.cta} />}

              <div className={styles.ctaRow}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    logEvent('engagement', 'dividend_cta_click', '/tax-benefit');
                    navigate('/tax-benefit');
                  }}
                  sx={{ background: '#000033', '&:hover': { background: '#001166' } }}
                >
                  ISA 계좌로 배당세 절세하기 →
                </Button>
              </div>

              <Adsense adClient="ca-pub-9359050149154028" adSlot="DIVIDEND_AD_SLOT" />

              <p className={styles.disclaimer}>
                이 계산기는 2024년 기준 데이터를 사용한 참고용 시뮬레이션입니다. 배당수익률·성장률은 과거 기준이며
                미래를 보장하지 않습니다. 월 납입금은 연초 일괄 투자 기준으로 계산하여 실제 적립식 투자 결과와
                차이가 있을 수 있습니다. 환율 변동, 세금 변경, ETF 운용보수는 반영되지 않았습니다.
                중요한 투자 결정은 전문가와 상담하세요.
              </p>
            </div>
          </Container>
        </div>
      )}
    </div>
  );
}

export default DividendPage;
